import React, { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  Textarea,
  Text,
  VStack,
  IconButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Checkbox,
  useDisclosure,
  Modal,
  ModalBody,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalCloseButton,
  Spinner,
  useToast,
  Collapse,
  Heading,
  List,
  ListItem,
  ListIcon,
} from '@chakra-ui/react';
import { MoonIcon, SunIcon, ChevronDownIcon, ChevronUpIcon, CheckCircleIcon } from '@chakra-ui/icons';
import appConfig from '../Config/appConfig';
import api from '../Api/api';
import ComponentFactory from '../Components/Factory/ComponentFactory';
import BackgroundWrapper from '../Components/BackgroundWrapper';
import ModalComponent from '../Components/ModalComponent';
import NavBarComponent from '../Components/NavBarComponent';
import ButtonComponent from '../Components/ButtonComponent';
import PageViewTracker from '../Components/PageViewTracker';

// Reusable Page Component (unchanged from original) origin.
const Page = ({ pageData, liveViewTheme, isTouchDevice, isMouseDevice, handleButtonAction, formData, updateFormData, isModalOpen, setIsModalOpen, modalContent, isLastPage }) => {
  const renderContent = (sections) => {
    if (!sections) {
      return <Text color="gray.500">No content to render</Text>;
    }
    return sections.map((section, sectionIndex) => {
      if (!section.type) {
        console.error(`Missing type for section at index ${sectionIndex}`, section);
        return (
          <Text key={sectionIndex} color="red.500" fontSize="sm">
            Invalid section structure
          </Text>
        );
      }
      if (section.type === 'button' && Array.isArray(section.cta)) {
        const hasHalfVariant = section.cta.some(ctaItem => ctaItem.variant?.includes('-half'));
        const Wrapper = hasHalfVariant ? Flex : Box;
        const wrapperProps = hasHalfVariant ? { justify: "space-between", gap: 4, width: "100%" } : {};
        return (
          <Wrapper key={`btns-wrapper-${sectionIndex}`} {...wrapperProps}>
            {section.cta.map((ctaItem, btnIndex) => (
              <ButtonComponent
                key={`cta-${btnIndex}`}
                action={ctaItem.action}
                label={ctaItem.label}
                ariaLabel={ctaItem.ariaLabel}
                tooltip={ctaItem.tooltip}
                variant={ctaItem.variant || 'solid'}
                icons={ctaItem.icons}
                onClick={() => handleButtonAction(ctaItem.action, ctaItem)}
                textDecoration={ctaItem.textDecoration}
                isTouchDevice={isTouchDevice}
                isMouseDevice={isMouseDevice}
              />
            ))}
          </Wrapper>
        );
      }
      
      return (
        <ComponentFactory
          key={section.id || `section-${sectionIndex}`}
          section={section}
          theme={liveViewTheme}
          priorDecisions={[]}
          formData={formData}
          updateFormData={updateFormData}
          handleButtonAction={handleButtonAction}
        />
      );
    });
  };

  return (
    <Box
      minWidth="390px"
      minHeight="calc(100vh - 120px)"
      bg="white"
      borderRadius="md"
      shadow="md"
      overflowY="auto"
      overflowX="hidden"
      display="flex"
      flexDirection="column"
      mr={isLastPage ? 0 : 5}
      flexShrink={0}
      my={5}
    >
      <BackgroundWrapper
        sections={pageData.sections}
        style={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Flex direction="column" height="100%">
          {pageData.sections?.map((section, index) => {
            if (section.type === 'navbar') {
              return (
                <NavBarComponent
                  key={index}
                  image={section.image}
                  buttons={section.button}
                />
              );
            }
            return null;
          })}
          <Flex
            flex="1"
            justify="center"
            align="center"
            overflow="hidden"
          >
            <Box
              width="390px"
              height="calc(100vh - 120px)"
              bg="white"
              borderRadius="md"
              shadow={['none', '0px 4px 32px 0px #00000026']}
              overflowY="auto"
              overflowX="hidden"
              display="flex"
              flexDirection="column"
            >
              <Box flex="1" py={4} m={4} display="flex" flexDirection="column">
                {renderContent(pageData.sections)}
              </Box>
            </Box>
          </Flex>
        </Flex>
        <ModalComponent
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          modalContent={modalContent}
          handleButtonAction={handleButtonAction}
        />
      </BackgroundWrapper>
    </Box>
  );
};

const Template = () => {
  const [jsonInput, setJsonInput] = useState('');
  const [parsedContent, setParsedContent] = useState(null);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({});
  const [liveViewTheme, setLiveViewTheme] = useState('light');
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [isMouseDevice, setIsMouseDevice] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState(null);
  const [programPrefix, setProgramPrefix] = useState('');
  const [taskType, setTaskType] = useState('');
  const [revision, setRevision] = useState('1.0');
  const [programId, setProgramId] = useState('5');
  const [taskName, setTaskName] = useState('');
  const [taskDescription, setTaskDescription] = useState('');
  const [ingestionMode, setIngestionMode] = useState('full');
  const [overwriteExisting, setOverwriteExisting] = useState(false);
  const { isOpen: isConfirmModalOpen, onOpen: onConfirmModalOpen, onClose: onConfirmModalClose } = useDisclosure();
  const { isOpen: showSavedData, onToggle: toggleSavedData } = useDisclosure();
  const [isSaving, setIsSaving] = useState(false);
  const [savedData, setSavedData] = useState(null);
  const toast = useToast();

  useEffect(() => {
    const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const hasMouse = 'onmousemove' in window;
    setIsTouchDevice(hasTouch);
    setIsMouseDevice(hasMouse);
  }, []);

  const handleJsonChange = (value) => {
    setJsonInput(value);
    setError('');
    setParsedContent(null);

    if (!value || value.trim() === '') {
      setError('JSON input is empty. Please provide valid JSON.');
      return;
    }

    try {
      const topLevelParsed = JSON.parse(value);
      let pagesToRender = [];

      if (topLevelParsed && topLevelParsed.data && Array.isArray(topLevelParsed.data)) {
        topLevelParsed.data.forEach(taskData => {
          if (taskData.StepsJson) {
            try {
              const steps = JSON.parse(taskData.StepsJson);
              steps.forEach(step => {
                if (step.SchemaData) {
                  try {
                    const schema = JSON.parse(step.SchemaData);
                    if (schema && (schema.sections || Array.isArray(schema))) {
                      pagesToRender.push(schema);
                    } else {
                      console.warn(`Invalid schema structure in step ${step.StepId}`);
                    }
                  } catch (innerErr) {
                    console.error(`Error parsing SchemaData in step ${step.StepId}:`, innerErr);
                    setError(`Error parsing SchemaData in step ${step.StepId}: ${innerErr.message}`);
                  }
                }
              });
            } catch (stepsErr) {
              console.error('Error parsing StepsJson:', stepsErr);
              setError(`Error parsing StepsJson: ${stepsErr.message}`);
            }
          }
        });
      } else if (topLevelParsed && Array.isArray(topLevelParsed.pages)) {
        pagesToRender = topLevelParsed.pages.filter(page => page && page.sections);
      } else if (topLevelParsed && topLevelParsed.sections) {
        pagesToRender = [topLevelParsed];
      } else if (Array.isArray(topLevelParsed)) {
        pagesToRender = topLevelParsed.filter(page => page && page.sections);
      } else {
        setError('Invalid JSON structure. Expected API response, a single page object, an array of pages, or an object with a "pages" array.');
        return;
      }

      if (pagesToRender.length === 0) {
        setError('No valid pages found in the JSON input.');
        return;
      }

      setParsedContent(pagesToRender);
    } catch (err) {
      setError('Invalid JSON. Please check your input: ' + err.message);
      setParsedContent(null);
    }
  };

  const updateFormData = (id, value, label = null, metric = null, flowSheetId = null) => {
    setFormData((prevData) => ({
      ...prevData,
      [id]: { value: value ?? "", label, metric, flowSheetId },
    }));
  };

  const handleButtonAction = async (action, buttonData) => {
    try {
      switch (action) {
        case 'modal':
          if (buttonData.actionType === 'close-app') {
            setIsModalOpen(false);
            setTimeout(() => {
              window.close();
            }, 2000);
          } else if (buttonData.actionType === 'close-modal') {
            setIsModalOpen(false);
          }
          break;
        case 'back':
          setModalContent({
            title: 'Work in Progress',
            content: [
              {
                type: 'paragraph',
                content: 'This feature is under development. Stay tuned!',
              },
            ],
          });
          setIsModalOpen(true);
          break;
        case 'next':
          setModalContent({
            title: 'Work in Progress',
            content: [
              {
                type: 'paragraph',
                content: 'Navigation to the next page is under development. Stay tuned!',
              },
            ],
          });
          setIsModalOpen(true);
          break;
        case 'submit':
          setModalContent({
            title: 'Submission Successful!',
            content: [
              {
                type: 'paragraph',
                content: 'Your data has been successfully submitted. Thank you for your feedback!',
              },
              {
                type: 'button',
                label: 'Close',
                action: 'modal',
                actionType: 'close-modal',
                ariaLabel: 'Close the confirmation message',
                tooltip: 'Click to close this message',
                variant: 'solid',
              },
            ],
          });
          setIsModalOpen(true);
          break;
        default:
          console.warn(`Unhandled action type: ${action}`);
      }
    } catch (error) {
      console.error(`Error handling action "${action}":`, error);
    }
  };

  const handleSave = () => {
    if (error || !parsedContent || parsedContent.length === 0) {
      toast({
        title: 'Validation Error',
        description: 'Please fix the JSON input before saving.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (!programPrefix.trim() || (ingestionMode === 'full' && (!taskType.trim() || !revision.trim()))) {
      toast({
        title: 'Missing deployment details',
        description: ingestionMode === 'full'
          ? 'Program Prefix, Task Type, and Revision are required.'
          : 'Program Prefix is required.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setSavedData(null);
    onConfirmModalOpen();
  };

  const handleConfirmSave = async () => {
    setIsSaving(true);

    try {
      const parsedJson = JSON.parse(jsonInput);
      let ingestionSteps = parsedJson;

      // The preview accepts API responses, but sp_SaveContent requires the
      // actual array of step objects as its JSON input.
      if (parsedJson && parsedJson.data && Array.isArray(parsedJson.data)) {
        ingestionSteps = parsedJson.data.flatMap((taskData) => {
          if (!taskData.StepsJson) return [];
          const steps = typeof taskData.StepsJson === 'string'
            ? JSON.parse(taskData.StepsJson)
            : taskData.StepsJson;
          return steps.map((step) => {
            if (!step.SchemaData) return step;
            return typeof step.SchemaData === 'string'
              ? JSON.parse(step.SchemaData)
              : step.SchemaData;
          });
        });
      } else if (parsedJson && Array.isArray(parsedJson.pages)) {
        ingestionSteps = parsedJson.pages;
      }

      if (!Array.isArray(ingestionSteps) || ingestionSteps.length === 0) {
        throw new Error('The JSON must contain a non-empty array of step objects.');
      }

      const payload = {
        ProgramPrefix: programPrefix.trim(),
        JsonData: JSON.stringify(ingestionSteps),
      };
      const endpoint = ingestionMode === 'content-only'
        ? `${appConfig.APIBaseURL}/api/v1/content/content-only`
        : `${appConfig.APIBaseURL}/api/v1/content`;

      if (ingestionMode === 'content-only') {
        payload.OverwriteExisting = overwriteExisting;
      } else {
        payload.TaskType = taskType.trim();
        payload.Revision = revision.trim();
        payload.ProgramId = Number(programId) || 5;
        payload.TaskName = taskName.trim() || programPrefix.trim();
        payload.TaskDescription = taskDescription.trim() || payload.TaskName;
      }

      const response = await api.post(endpoint, payload);
      const responseData = response.data;

      if (!responseData) {
        throw new Error('Empty response data received from API');
      }

      if (response.status === 200) {
        const savedRow = responseData.data?.[0] || responseData.data || null;
        if (ingestionMode === 'content-only') {
          setSavedData({
            taskId: null,
            taskCode: programPrefix.trim(),
            revision: null,
            mode: 'content-only',
            steps: (Array.isArray(responseData.data) ? responseData.data : []).map((item) => ({
              stepCode: item.Title,
              contentTitle: `${item.Action || 'PROCESSED'} (Content ${item.Id})`,
            })),
          });
        }
        let savedSteps = savedRow?.StepsJson || [];
        if (typeof savedSteps === 'string') {
          savedSteps = JSON.parse(savedSteps);
        }
        if (ingestionMode === 'full') setSavedData(savedRow ? {
          taskId: savedRow.TaskId,
          taskCode: savedRow.ProgramPrefix,
          revision: savedRow.Revision,
          mode: 'full',
          steps: savedSteps.map((step) => ({
            stepId: step.StepId,
            stepCode: step.StepCode,
            contentTitle: step.ContentTitle,
          })),
        } : null);

        // Show toast with response message
        toast({
          title: 'Success!',
          description: responseData.message || 'Data saved successfully.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });

        // Close the confirmation modal
        onConfirmModalClose();
      } else {
        throw new Error('Unexpected response status: ' + response.status);
      }
    } catch (apiError) {
      console.error('Failed to save data:', apiError);
      setSavedData(null);

      toast({
        title: 'Error Saving Data',
        description: apiError.message || 'There was an issue calling the API. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box height="100vh" overflow="hidden">
      <PageViewTracker pageName="TemplatePage" />
      <Flex height="100%">
        <VStack
          align="stretch"
          width="50%"
          spacing={4}
          p={4}
          borderRight="1px solid var(--border-color)"
          flexShrink={0}
          overflowY="auto"
        >
          <Flex justify="space-between" align="center" pb={4}>
            <Text fontWeight="bold" fontSize="xl">
              Input JSON Array
            </Text>
            <IconButton
              aria-label="Toggle live view theme"
              icon={liveViewTheme === 'light' ? <MoonIcon /> : <SunIcon />}
              onClick={() => setLiveViewTheme((prev) => (prev === 'light' ? 'dark' : 'light'))}
              size="sm"
            />
          </Flex>
          <Textarea
            placeholder="Paste JSON ARRAY here."
            rows={25}
            value={jsonInput}
            onChange={(e) => handleJsonChange(e.target.value)}
            aria-label="JSON array input area"
          />
          {error && (
            <Text color="red.500" fontSize="sm" aria-live="polite">
              {error}
            </Text>
          )}

          <Box mt={4} pt={4} borderTop="1px solid var(--border-color)">
            <Text fontWeight="bold" fontSize="xl" mb={4}>
              Save to Database
            </Text>
            <VStack spacing={3}>
              <FormControl>
                <FormLabel>Program Prefix</FormLabel>
                <Input
                  value={programPrefix}
                  onChange={(e) => setProgramPrefix(e.target.value)}
                  placeholder="e.g., TASKREFERENCEID"
                />
              </FormControl>
              <FormControl>
                <FormLabel fontWeight="bold">What should be written?</FormLabel>
                <Select value={ingestionMode} onChange={(e) => setIngestionMode(e.target.value)}>
                  <option value="full">Everything: task, steps, content, links, and rules</option>
                  <option value="content-only">Content only: content rows, no task/steps/rules</option>
                </Select>
              </FormControl>
              {ingestionMode === 'full' && (
                <>
                  <FormControl>
                    <FormLabel>Program ID</FormLabel>
                    <Input
                      type="number"
                      value={programId}
                      onChange={(e) => setProgramId(e.target.value)}
                      placeholder="e.g., 5"
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Task Type</FormLabel>
                    <Input
                      value={taskType}
                      onChange={(e) => setTaskType(e.target.value)}
                      placeholder="e.g., PROGRAMINTRO"
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Revision</FormLabel>
                    <Input
                      value={revision}
                      onChange={(e) => setRevision(e.target.value)}
                      placeholder="e.g., 1.0"
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Task Name</FormLabel>
                    <Input
                      value={taskName}
                      onChange={(e) => setTaskName(e.target.value)}
                      placeholder="Defaults to Program Prefix"
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Task Description</FormLabel>
                    <Input
                      value={taskDescription}
                      onChange={(e) => setTaskDescription(e.target.value)}
                      placeholder="Defaults to Task Name"
                    />
                  </FormControl>
                </>
              )}
              {ingestionMode === 'content-only' && (
                <Box borderWidth="1px" borderRadius="md" p={3} bg="orange.50" borderColor="orange.200">
                  <Text fontSize="sm" mb={2}>
                    This writes only Content rows using {programPrefix || 'PREFIX'}-{'{title}'}. It does not create or update Task, TaskRevision, Step, StepContent, or BusinessRule rows.
                  </Text>
                  <Checkbox
                    isChecked={overwriteExisting}
                    onChange={(e) => setOverwriteExisting(e.target.checked)}
                  >
                    Overwrite matching existing content
                  </Checkbox>
                </Box>
              )}
              <Button
                colorScheme="teal"
                width="100%"
                onClick={handleSave}
                isLoading={isSaving}
                loadingText="Saving..."
                isDisabled={!parsedContent || isSaving}
              >
                Save to Database
              </Button>
            </VStack>
          </Box>

          {savedData && (
            <Box mt={4} pt={4} borderTop="1px solid var(--border-color)">
              <Flex justify="space-between" align="center">
                <Heading size="md">
                  Saved Data <CheckCircleIcon color="green.500" />
                </Heading>
                <IconButton
                  aria-label="Toggle saved data"
                  icon={showSavedData ? <ChevronUpIcon /> : <ChevronDownIcon />}
                  onClick={toggleSavedData}
                  variant="ghost"
                  size="sm"
                />
              </Flex>
              <Collapse in={showSavedData} animateOpacity>
                <Box p={4} mt={2} bg="gray.50" borderRadius="md" fontSize="sm">
                  <Text>
                    **Task ID:** {savedData.taskId}
                  </Text>
                  <Text>
                    **Task Code:** {savedData.taskCode}
                  </Text>
                  <Text mb={2}>
                    **Revision:** {savedData.revision}
                  </Text>
                  <Text fontWeight="bold" mt={2}>
                    Steps & Content:
                  </Text>
                  <List spacing={1} mt={2}>
                    {savedData.steps.map((step, index) => (
                      <ListItem key={index}>
                        <ListIcon as={CheckCircleIcon} color="green.500" />
                        **{step.stepCode}** linked to **{step.contentTitle}**
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </Collapse>
            </Box>
          )}
        </VStack>

        <Box
          width="50%"
          height="100vh"
          bg="gray.100"
          p={5}
          overflowX="auto"
          overflowY="hidden"
          flexGrow={1}
        >
          <Text fontSize="lg" fontWeight="bold" mb={4}>
            Rendered Output
          </Text>
          <Flex
            minWidth="max-content"
            height="100%"
            overflowX="hidden"
            overflowY="hidden"
            flexWrap="nowrap"
            justifyContent="flex-start"
            alignItems="center"
          >
            {parsedContent && parsedContent.length > 0 ? (
              parsedContent.map((pageData, index) => (
                <Page
                  key={index}
                  pageData={pageData}
                  liveViewTheme={liveViewTheme}
                  isTouchDevice={isTouchDevice}
                  isMouseDevice={isMouseDevice}
                  handleButtonAction={handleButtonAction}
                  formData={formData}
                  updateFormData={updateFormData}
                  isModalOpen={isModalOpen}
                  setIsModalOpen={setIsModalOpen}
                  modalContent={modalContent}
                  isLastPage={index === parsedContent.length - 1}
                />
              ))
            ) : (
              <Flex height="100%" width="100%" justify="center" align="center" p={4}>
                <Text color="gray.500" fontSize="xl">
                  Paste a JSON array of page objects on the left to get started.
                </Text>
              </Flex>
            )}
          </Flex>
        </Box>
      </Flex>

      <Modal isOpen={isConfirmModalOpen} onClose={onConfirmModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirm Save</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb={4}>
              Confirm the database operation before continuing.
            </Text>
            <Box p={4} bg="gray.50" borderRadius="md" fontSize="sm">
              <Text fontWeight="bold">Program Prefix: </Text>
              <Text mb={2}>{programPrefix}</Text>
              <Text fontWeight="bold">Mode: </Text>
              <Text mb={2} color={ingestionMode === 'content-only' ? 'orange.700' : 'teal.700'}>
                {ingestionMode === 'content-only'
                  ? 'Content only'
                  : 'Full deployment: task, steps, content, links, and business rules'}
              </Text>
              {ingestionMode === 'full' ? (
                <>
                  <Text fontWeight="bold">Task Type: </Text>
                  <Text mb={2}>{taskType}</Text>
                  <Text fontWeight="bold">Revision: </Text>
                  <Text mb={2}>{revision}</Text>
                </>
              ) : (
                <Text color="orange.700">
                  Existing content is {overwriteExisting ? 'updated when titles match.' : 'left unchanged when titles match.'}
                </Text>
              )}
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onConfirmModalClose}>
              Cancel
            </Button>
            <Button colorScheme="teal" onClick={handleConfirmSave} isLoading={isSaving}>
              Confirm & Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {isSaving && (
        <Flex
          position="fixed"
          top="0"
          left="0"
          width="100vw"
          height="100vh"
          bg="rgba(0, 0, 0, 0.5)"
          justify="center"
          align="center"
          zIndex="overlay"
        >
          <Spinner
            size="xl"
            thickness="4px"
            color="teal.500"
          />
        </Flex>
      )}
    </Box>
  );
};

export default Template;